import { useOrganizationAvailableForInvitationUsers } from '@forwardflow/hooks/customHooks/organization/useGetAvailableForInvitationUsers'
import { userOrganizationUsersForInvitationFilter } from '@forwardflow/store/filter/userOrganizationUsersForInvitationFilter'
import { Button } from '@forwardflow/ui/Button'
import { SearchField } from '@forwardflow/ui/SearchField'
import { message, Modal } from 'antd'
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import UserListItem from './UserListItem'
import { UserService } from '@forwardflow/services/UserService'
import {
   useAreYouAnAgentModalStore,
   useInviteAgentModalStore,
} from '@forwardflow/store/modal'
import debounce from 'lodash/debounce'

import Icon from '@forwardflow/ui/Icons'
import { useGetUserSubscription } from '@forwardflow/hooks/customHooks/subscription/useGetUserSubscription'
import { useOrganizationUsersInfinite } from '@forwardflow/hooks/customHooks/organization'
import SubscriptionService from '@forwardflow/services/SubscriptionService'
import { useAppStore } from '@forwardflow/store'

export const InviteAgentModal = () => {
   const [isInviteSent, setIsInviteSent] = useState(false)
   const [isLoading, setIsLoading] = useState(false)
   const [isSubmitLoading, setIsSubmitLoading] = useState(false)
   const [isSearching, setIsSearching] = useState(false)
   const { isInviteAgentModalOpen, closeInviteAgentModal, setWasInviteSent } =
      useInviteAgentModalStore()
   const [selectedUsers, setSelectedUsers] = useState<any[]>([])
   const [searchQuery, setSearchQuery] = useState('')
   const [selectedRole, setSelectedRole] = useState<'Admin' | 'Agent'>('Agent')
   const [step, setStep] = useState<'select' | 'review'>('select')
   const [linkedEmails, setLinkedEmails] = useState<Record<string, string>>({})

   const { openAreYouAnAgentModal } = useAreYouAnAgentModalStore()

   const { currentOrganization } = useAppStore()
   const { data: subscriptionData, mutate: mutateSubscription } = useGetUserSubscription()
   const acceptedUsersHook = useOrganizationUsersInfinite({ invitationStatus: 'accepted' })
   const pendingUsersHook = useOrganizationUsersInfinite({ invitationStatus: 'pending' })

   const totalSeatCount = subscriptionData?.organizationSubscription?.seatCounts ?? 0
   const maxSeats = subscriptionData?.organizationSubscription?.pricingTier?.maxSeats ?? 100
   const billingPeriod = subscriptionData?.organizationSubscription?.billingPeriod ?? 'monthly'
   const currentUserCount = acceptedUsersHook.totalItems + pendingUsersHook.totalItems
   const remainingSeats = Math.max(0, totalSeatCount - currentUserCount)
   const isSeatLimitReached = totalSeatCount > 0 && remainingSeats === 0

   const [additionalSeats, setAdditionalSeats] = useState(1)
   const [isBuyingSeats, setIsBuyingSeats] = useState(false)
   const [showBuySeats, setShowBuySeats] = useState(false)

   const handleBuySeats = async () => {
      if (!currentOrganization || isBuyingSeats || additionalSeats < 1) return
      setIsBuyingSeats(true)
      try {
         await SubscriptionService.addSeats(currentOrganization, additionalSeats)
         message.success(`${additionalSeats} seat${additionalSeats > 1 ? 's' : ''} added successfully`)
         setShowBuySeats(false)
         setAdditionalSeats(1)
         mutateSubscription()
      } catch (error) {
         console.error(error)
         message.error('Failed to add seats')
      } finally {
         setIsBuyingSeats(false)
      }
   }

   const { page, limit, search, setQueryParams } = userOrganizationUsersForInvitationFilter()
   const { data, mutate, isLoading: isApiLoading } = useOrganizationAvailableForInvitationUsers({
      page: page,
      limit: limit,
      search: search, 
   })

   const [allUsers, setAllUsers] = useState<any[]>([])

   useEffect(() => {
      if (isInviteAgentModalOpen) {
         setIsInviteSent(false)
         setSelectedUsers([])
         setIsLoading(false)
         setIsSubmitLoading(false)
         setIsSearching(false)
         setAllUsers([])
         setSearchQuery('')
         setSelectedRole('Agent')
         setStep('select')
         setLinkedEmails({})
         setQueryParams({ page: 1, limit, search: '' })
         setShowBuySeats(false)
         setAdditionalSeats(1)
      }
   }, [isInviteAgentModalOpen, setQueryParams, limit])

   useEffect(() => {
      if (isInviteAgentModalOpen && page === 1) {
         const timer = setTimeout(() => {
            mutate()
         }, 50)
         return () => clearTimeout(timer)
      }
   }, [isInviteAgentModalOpen, page, mutate])

   useEffect(() => {
      if (data?.data && isInviteAgentModalOpen) {
         setAllUsers((prevUsers) => {
            if (page === 1) {
               return data.data
            }
            const combined = [...prevUsers, ...data.data]
            const unique = combined.filter(
               (user, index, self) =>
                  index === self.findIndex((u) => u.email === user.email)
            )
            return unique
         })
         setIsLoading(false)
         setIsSearching(false) 
      }
   }, [data, isInviteAgentModalOpen, page])

   useEffect(() => {
      if (isApiLoading && searchQuery && (page ?? 1) === 1) {
         setIsSearching(true)
      } else if (!isApiLoading) {
         setIsSearching(false)
      }
   }, [isApiLoading, searchQuery, page])

   useEffect(() => {
      if (isInviteAgentModalOpen && (page ?? 1) > 1) {
         mutate()
      }
   }, [page, isInviteAgentModalOpen, mutate])

   const debouncedSearch = useCallback(
      debounce((value: string) => {
         setIsSearching(true)
         setQueryParams({ 
            page: 1, 
            limit, 
            search: value,
            sorts: '',
            fields: '',
            filters: '',
            includes: ''
         })
         setAllUsers([])
      }, 500),
      [setQueryParams, limit]
   )

   const handleSearch = (value: string) => {
      setSearchQuery(value)
      if (value.trim()) {
         debouncedSearch(value)
      } else {
         setIsSearching(false)
         setQueryParams({ 
            page: 1, 
            limit, 
            search: '',
            sorts: '',
            fields: '',
            filters: '',
            includes: ''
         })
         setAllUsers([])
      }
   }

   const scrollContainerRef = useRef<HTMLDivElement>(null)

   const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const element = e.target as HTMLDivElement
      const hasReachedBottom =
         element.scrollHeight - element.scrollTop <= element.clientHeight + 10
      const hasMoreData = allUsers.length < (data?.meta?.totalItems ?? 0)

      if (hasReachedBottom && hasMoreData && !isLoading) {
         setIsLoading(true)
         setQueryParams({
            page: (page ?? 0) + 1,
            limit,
            search,
            sorts: '',
            fields: '',
            filters: '',
            includes: ''
         })
      }
   }

   const users = useMemo(() => {
      return allUsers.filter(
         (user: any) =>
            user.invitationStatus === null ||
            user.invitationStatus === 'pending'
      )
   }, [allUsers])

   const handleUserSelect = (user: any) => {
      setSelectedUsers((prevUsers) => {
         const isUserSelected = prevUsers.some((u) => u.email === user.email)
         if (isUserSelected) {
            return prevUsers.filter((u) => u.email !== user.email)
         }
         if (totalSeatCount > 0 && prevUsers.length >= remainingSeats) {
            return prevUsers
         }
         return [...prevUsers, user]
      })
   }

   const sendUserInvite = async (emailIds: string[], role: string, linkedEmails?: Record<string, string>) => {
      setIsSubmitLoading(true)
      try {
         await UserService.userSendInvitation(emailIds, role, linkedEmails)
         message.success('User invitation sent successfully')
         setIsInviteSent(true)
         setWasInviteSent(true)
      } catch (error) {
         console.error(error)
         message.error('Failed to send user invitation')
      } finally {
         setIsSubmitLoading(false)
      }
   }

   const handleSubmit = () => {
      if (selectedUsers.length > 0) {
         const userEmails = selectedUsers.map((user: any) => user.email)
         const cleanLinkedEmails = Object.fromEntries(
            Object.entries(linkedEmails).filter(([_, v]) => v.trim() !== '')
         )
         sendUserInvite(userEmails, selectedRole, cleanLinkedEmails)
      }
   }

   const InviteSentComponent = () => {
      return (
         <div className="flex flex-col gap-y-5 !bg-tile_1_bg_img">
            <div className="w-[100px] h-[100px] rounded-full bg-primary/40 flexc mx-auto mt-[110px]">
               <Icon name="Check" className="scale-[5] fill-white" />
            </div>
            <p className="mb-20 text-[24px] font-semibold text-center">
               User Invites sent!
            </p>
         </div>
      )
   }

   return (
      <>
         <Modal
            className={`${!isInviteSent ? '' : 'p-0'}  ff-modal lg:!w-[560px] `}
            title={
               !isInviteSent
                  ? step === 'review'
                     ? 'Review & Set Login Emails'
                     : 'Select agents to send email invites'
                  : ''
            }
            open={isInviteAgentModalOpen}
            onCancel={closeInviteAgentModal}
            footer={null}
         >
            <>
               {isInviteSent ? (
                  <InviteSentComponent />
               ) : (
                  <>
                     {step === 'select' && (
                     <>
                     <p className="text-[#818181] text-[12px] font-medium">
                        Select agents to send email invites
                     </p>

                     <div className="mt-4 mb-4">
                        <p className="text-[#818181] text-[12px] font-medium mb-3">
                           Select Role
                        </p>
                        <div className="flex gap-4 mb-4">
                           <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                 type="radio"
                                 name="role"
                                 value="Admin"
                                 checked={selectedRole === 'Admin'}
                                 onChange={(e) => setSelectedRole(e.target.value as 'Admin' | 'Agent')}
                                 className="w-4 h-4 accent-primary cursor-pointer"
                              />
                              <span className="text-[14px] font-medium text-white">Admin</span>
                           </label>
                           <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                 type="radio"
                                 name="role"
                                 value="Agent"
                                 checked={selectedRole === 'Agent'}
                                 onChange={(e) => setSelectedRole(e.target.value as 'Admin' | 'Agent')}
                                 className="w-4 h-4 accent-primary cursor-pointer"
                              />
                              <span className="text-[14px] font-medium text-white">Agent</span>
                           </label>
                        </div>
                     </div>

                     {isSeatLimitReached && !showBuySeats && (
                        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
                           <div className="flex items-center justify-between">
                              <div>
                                 <p className="text-red-400 text-[13px] font-medium">
                                    All seats are occupied ({currentUserCount}/{totalSeatCount})
                                 </p>
                                 <p className="text-red-400/70 text-[11px] mt-1">
                                    Purchase additional seats to invite more users.
                                 </p>
                              </div>
                              <button
                                 type="button"
                                 onClick={() => setShowBuySeats(true)}
                                 className="text-[12px] font-medium text-primary hover:text-primary/80 transition-colors whitespace-nowrap ml-3"
                              >
                                 Buy Seats
                              </button>
                           </div>
                        </div>
                     )}

                     {showBuySeats && (
                        <div className="mb-4 rounded-lg border border-[#A684FF]/30 bg-[#A684FF]/5 px-4 py-3 space-y-2">
                           <div className="flex items-center justify-between">
                              <p className="text-white/90 text-[13px] font-medium">
                                 Add Seats
                              </p>
                              <button
                                 type="button"
                                 onClick={() => {
                                    setShowBuySeats(false)
                                    setAdditionalSeats(1)
                                 }}
                                 className="text-white/40 hover:text-white/70 transition-colors"
                              >
                                 <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                 </svg>
                              </button>
                           </div>
                           <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                 <button
                                    type="button"
                                    onClick={() => setAdditionalSeats((prev) => Math.max(1, prev - 1))}
                                    className="w-8 h-8 rounded-md border border-white/20 bg-white/5 text-white flex items-center justify-center hover:bg-white/10 transition-colors"
                                 >
                                    -
                                 </button>
                                 <span className="text-white font-medium text-[16px] w-8 text-center">
                                    {additionalSeats}
                                 </span>
                                 <button
                                    type="button"
                                    onClick={() => setAdditionalSeats((prev) => Math.min(maxSeats - totalSeatCount, prev + 1))}
                                    disabled={totalSeatCount + additionalSeats >= maxSeats}
                                    className="w-8 h-8 rounded-md border border-white/20 bg-white/5 text-white flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                 >
                                    +
                                 </button>
                              </div>
                              <p className="text-white/60 text-[12px]">
                                 +${(additionalSeats * 50).toFixed(2)}/{billingPeriod === 'yearly' ? 'yr' : 'mo'}
                              </p>
                           </div>
                           <Button
                              onClick={handleBuySeats}
                              isLoading={isBuyingSeats}
                              className="!mt-2 !h-9 !text-[13px]"
                           >
                              Purchase {additionalSeats} Seat{additionalSeats > 1 ? 's' : ''}
                           </Button>
                        </div>
                     )}

                     {!isSeatLimitReached && totalSeatCount > 0 && (
                        <div className="mb-4 rounded-lg border border-[#A684FF]/30 bg-[#A684FF]/5 px-4 py-3">
                           <div className="flex items-center justify-between">
                              <p className="text-white/80 text-[13px]">
                                 {remainingSeats} seat{remainingSeats !== 1 ? 's' : ''} remaining ({currentUserCount}/{totalSeatCount} used)
                              </p>
                              <button
                                 type="button"
                                 onClick={() => setShowBuySeats(true)}
                                 className="text-[12px] font-medium text-primary hover:text-primary/80 transition-colors"
                              >
                                 Buy More
                              </button>
                           </div>
                        </div>
                     )}

                     <div className="mb-4">
                        <SearchField
                           placeholder="Search Users"
                           className="!rounded-lg !text-[14px] !w-full !max-w-full !bg-white/5 !border !border-white/10 !h-11"
                           value={searchQuery}
                           onChange={handleSearch}
                           disabled={isSeatLimitReached}
                        />
                     </div>

                     <p className="mb-4 text-[10px] text-[#818181]">
                        Selected {selectedUsers.length}/{remainingSeats > 0 ? remainingSeats : 0} available seats
                     </p>
                     <div
                        ref={scrollContainerRef}
                        className="flex flex-col overflow-y-auto gap-y-1 max-h-[378px]"
                        onScroll={handleScroll}
                     >
                        {isSearching || (isApiLoading && allUsers.length === 0) ? (
                           <div className="flex flex-col gap-y-1">
                              {[...Array(5)].map((_, i) => (
                                 <div
                                    key={i}
                                    className="flex items-center bg-[#828282]/20 border border-white/20 py-4 px-[22px] rounded-lg animate-pulse"
                                 >
                                    <div className="w-4 h-4 rounded-full bg-white/10 shrink-0 mr-4" />
                                    <div className="flex flex-col gap-1.5 flex-1 mr-2">
                                       <div className="h-3.5 w-24 bg-white/10 rounded" />
                                    </div>
                                    <div className="flex flex-col gap-1.5 w-[60%]">
                                       <div className="h-3 w-40 bg-white/10 rounded" />
                                       <div className="h-3 w-28 bg-white/10 rounded" />
                                    </div>
                                 </div>
                              ))}
                           </div>
                        ) : searchQuery && users?.length === 0 ? (
                           <div className="text-center py-8 text-white/70">No results found</div>
                        ) : users?.length > 0 ? (
                           users.map((data, index) => (
                              <UserListItem
                                 key={index}
                                 user={data}
                                 isSelected={selectedUsers.includes(data)}
                                 onUserSelect={() => {
                                    if (data.isAdmin) {
                                       const userToHandle = data
                                       const isCurrentlySelected =
                                          selectedUsers.includes(userToHandle)

                                       openAreYouAnAgentModal()

                                       const unsubscribe =
                                          useAreYouAnAgentModalStore.subscribe(
                                             (state: any) => {
                                                if (
                                                   !state.isAreYouAnAgentModalOpen
                                                ) {
                                                   if (
                                                      state.isUserAgent &&
                                                      !isCurrentlySelected
                                                   ) {
                                                      handleUserSelect(
                                                         userToHandle
                                                      )
                                                   } else if (
                                                      !state.isUserAgent &&
                                                      isCurrentlySelected
                                                   ) {
                                                      setSelectedUsers((prev) =>
                                                         prev.filter(
                                                            (user) =>
                                                               user.id !==
                                                               userToHandle.id
                                                         )
                                                      )
                                                   }
                                                   unsubscribe()
                                                }
                                             }
                                          )
                                    } else {
                                       handleUserSelect(data)
                                    }
                                 }}
                              />
                           ))
                        ) : (
                           <div className="text-center py-8 text-white/70">No users to invite</div>
                        )}
                        {isLoading && !isSearching && (
                           <div className="flex flex-col gap-y-1">
                              {[...Array(3)].map((_, i) => (
                                 <div
                                    key={i}
                                    className="flex items-center bg-[#828282]/20 border border-white/20 py-4 px-[22px] rounded-lg animate-pulse"
                                 >
                                    <div className="w-4 h-4 rounded-full bg-white/10 shrink-0 mr-4" />
                                    <div className="flex flex-col gap-1.5 flex-1 mr-2">
                                       <div className="h-3.5 w-24 bg-white/10 rounded" />
                                    </div>
                                    <div className="flex flex-col gap-1.5 w-[60%]">
                                       <div className="h-3 w-40 bg-white/10 rounded" />
                                       <div className="h-3 w-28 bg-white/10 rounded" />
                                    </div>
                                 </div>
                              ))}
                           </div>
                        )}
                     </div>
                     </>
                     )}

                     {step === 'review' && (
                        <>
                           <button
                              type="button"
                              onClick={() => setStep('select')}
                              className="flex items-center gap-1 text-[12px] text-white/50 hover:text-white/70 transition-colors mb-3"
                           >
                              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                              Back to selection
                           </button>
                           <div className="flex flex-col gap-y-3 max-h-[420px] overflow-y-auto">
                              <p className="text-[12px] text-white/50 mb-2">
                                 Optionally set a different login email for each user. If left empty, the CRM email will be used.
                              </p>
                              {selectedUsers.map((user: any) => (
                                 <div key={user.email} className="flex flex-col gap-2 bg-white/5 rounded-lg p-3 border border-white/10">
                                    <div className="flex items-center justify-between">
                                       <div>
                                          <p className="text-[14px] font-medium text-white">{user.firstName} {user.lastName}</p>
                                          <p className="text-[12px] text-white/50">{user.email}</p>
                                       </div>
                                       <button
                                          type="button"
                                          onClick={() => {
                                             setSelectedUsers(prev => prev.filter(u => u.email !== user.email))
                                             setLinkedEmails(prev => {
                                                const next = { ...prev }
                                                delete next[user.email]
                                                return next
                                             })
                                          }}
                                          className="text-[11px] text-red-400/70 hover:text-red-400 transition-colors"
                                       >
                                          Remove
                                       </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                       <input
                                          type="email"
                                          placeholder="Login email (optional)"
                                          value={linkedEmails[user.email] || ''}
                                          onChange={(e) => setLinkedEmails(prev => ({
                                             ...prev,
                                             [user.email]: e.target.value
                                          }))}
                                          className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-white/30 outline-none focus:border-primary/50 transition-colors"
                                       />
                                       {linkedEmails[user.email] && (
                                          <button
                                             type="button"
                                             onClick={() => setLinkedEmails(prev => {
                                                const next = { ...prev }
                                                delete next[user.email]
                                                return next
                                             })}
                                             className="text-white/40 hover:text-white/70 transition-colors p-1"
                                          >
                                             <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M18 6L6 18M6 6l12 12" /></svg>
                                          </button>
                                       )}
                                    </div>
                                 </div>
                              ))}
                           </div>
                        </>
                     )}

                     {step === 'select' ? (
                        <Button
                           onClick={() => setStep('review')}
                           isDisabled={isLoading || isSeatLimitReached || selectedUsers.length === 0}
                           className="!mt-8"
                        >
                           Next — Review Invites
                        </Button>
                     ) : (
                        <Button
                           onClick={handleSubmit}
                           isLoading={isSubmitLoading}
                           isDisabled={selectedUsers.length === 0}
                           className="!mt-8"
                        >
                           Send Invites
                        </Button>
                     )}
                  </>
               )}
            </>
         </Modal>
      </>
   )
}
